import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@uidotdev/usehooks';
import { LazyMotion, MotionConfig } from 'motion/react';

// project import
import GlobalAddButton from 'components/buttons/GlobalAddButton';
import { matchCreateRoute } from 'components/create-dialog/registry';
import ExportRibbon from 'components/export-ribbon/ExportRibbon';
import { LinkPeekHost } from 'components/global-search/LinkPeekHost';
import Loader from 'components/Loader';
import OnboardingAssistant from 'components/OnboardingAssistant';
import PageTitle from 'components/PageTitle';
import useConfig from 'hooks/useConfig';
import { getFullscreenPreferences } from 'hooks/useFocusedEditMode';
import { useIsMobile } from 'hooks/useIsMobile';
import { useUiDesign } from 'hooks/useUiDesign';
import { useDocumentThemeMark, useUiTheme } from 'hooks/useUiTheme';
import NewDesignLayout from 'layout/NewDesignLayout';
import { AiChatSessionProvider } from 'pages/ai-chat/AiChatSessionProvider';
import FloatingAiAssistant from 'pages/ai-chat/floating/FloatingAiAssistant';
import MainRoutes from 'routes/MainRoutes';
import { useDispatch, useSelector } from 'store';
import { useGetUserDataQuery } from 'store/api/user';
import { DRAWER_OPEN_QUERY, openDrawer } from 'store/reducers/menu';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { cn } from 'utils/cn';
import { readSidebarExpanded, rememberSidebarExpanded } from 'utils/sidebarExpanded';
import Drawer from './Drawer';
import SubDrawer from './drawers/SubDrawer';
import TemporaryDrawer from './drawers/TemporaryDrawer';
import { focusedEditKey, resolveFocusedEdit } from './focusedEdit';
import Footer from './Footer';
import Header from './Header';
import { MAIN_CONTENT_AREA_ID } from './useContentAreaRect';

/** Motion's features for the floating AI panel, the chunk the new design's frame loads too: fetched once it mounts. */
const motionFeatures = () => import('layout/NewDesignLayout/motionFeatures').then((module) => module.default);

// ==============================|| MAIN LAYOUT ||============================== //

function MainLayout() {
    const opensExpanded = useMediaQuery(DRAWER_OPEN_QUERY);
    const isMobile = useIsMobile();
    const { isNew, isLoading: isDesignLoading } = useUiDesign();
    const theme = useUiTheme();

    const { pathname, key: locationKey } = useLocation();

    const { miniDrawer } = useConfig();
    const dispatch = useDispatch();

    const menu = useSelector((state) => state.menu);
    // A tab left open reads the user again as it comes back: preferences are saved whole, from this copy.
    useGetUserDataQuery(undefined, { refetchOnFocus: true, refetchOnReconnect: true });
    const { focusedEditMode: storeFocusedEdit, fullscreenMode, focusedEditDeclinedFor } = menu;
    // Memoized: the layout re-renders on every menu dispatch, and the route match walks the whole tree.
    const { focusedEditMode, isFullscreen } = useMemo(
        () =>
            resolveFocusedEdit(
                [MainRoutes],
                pathname,
                { focusedEditMode: storeFocusedEdit, fullscreenMode },
                (pageKey) => getFullscreenPreferences()[pageKey],
                focusedEditDeclinedFor === locationKey,
                // In the new design a create URL renders no form: it lands on another page and opens the dialog.
                isNew && matchCreateRoute(pathname) !== null
            ),
        [pathname, storeFocusedEdit, fullscreenMode, focusedEditDeclinedFor, locationKey, isNew]
    );
    const pageKey = useMemo(() => focusedEditKey([MainRoutes], pathname), [pathname]);
    // A timecard form's answer box sits at the page's right, where a corner panel would cover it.
    const isTimecardForm = focusedEditMode && pageKey === 'timecards';

    const handleDrawerToggle = () => {
        if (isMobile) {
            dispatch(openDrawer({ drawerOpen: false }));
            return;
        }
        rememberSidebarExpanded(!menu.drawerOpen);
        dispatch(openDrawer({ drawerOpen: !menu.drawerOpen }));
    };

    // The store starts at the viewport's width; this follows the viewport across the breakpoint, unless the
    // user chose a state of their own. It runs again at the mobile breakpoint too: the stored choice applies
    // only at desktop widths. The new design's sidebar has its own default width, so this stays out of its way.
    useEffect(() => {
        if (!miniDrawer && !isNew) {
            dispatch(openDrawer({ drawerOpen: readSidebarExpanded() ?? opensExpanded }));
        }
    }, [dispatch, miniDrawer, opensExpanded, isNew, isMobile]);

    // TODO(react-doctor: no-effect-event-handler): reacts to config-driven
    // `miniDrawer` flag (and viewport via isMobile) on mount/config change,
    // not to a direct user event. Intentionally kept in an effect.
    useEffect(() => {
        if (miniDrawer) {
            dispatch(openDrawer({ drawerOpen: false }));
        }
    }, [dispatch, miniDrawer, isMobile]);

    useEffect(() => {
        dispatch(closeSubDrawer());
    }, [dispatch, pathname]);

    // Marks the document with the design, for the stylesheet to theme the new one. Dropped on unmount,
    // so the sign-in page never keeps it.
    useEffect(() => {
        if (isDesignLoading) return;
        const root = document.documentElement;
        root.dataset.design = isNew ? 'new' : 'classic';
        return () => {
            delete root.dataset.design;
        };
    }, [isNew, isDesignLoading]);

    useDocumentThemeMark(isNew, theme);

    const intl = useIntl();
    const defaultPageTitle = intl.formatMessage({ id: 'default-page-title' });

    // Held until the user's preferences are known, so the design never flips after the first paint.
    if (isDesignLoading) {
        return <Loader />;
    }
    if (isNew) {
        return (
            <NewDesignLayout
                focusedEditMode={focusedEditMode}
                isFullscreen={isFullscreen}
                isTimecardForm={isTimecardForm}
            />
        );
    }

    // `overflow-x-hidden`: an open temporary drawer would otherwise overflow the row. The AI panel docked to the side
    // narrows the row by its width.
    return (
        <div
            className={cn(
                'flex h-full w-[calc(100%-var(--ai-side-dock-width,0px))] overflow-x-hidden',
                focusedEditMode ? 'bg-grey-200' : 'bg-paper'
            )}
        >
            <PageTitle title={defaultPageTitle} showBreadcrumb breadcrumbContent="" />
            {!isFullscreen && <Drawer open={menu.drawerOpen} handleDrawerToggle={handleDrawerToggle} />}
            {!isFullscreen && <SubDrawer />}
            {/* `min-w-0`: the page's own min-content width must not push the row past the viewport. */}
            <main className="flex h-full min-w-0 flex-1 flex-col">
                <Header focusedMode={isFullscreen} />
                <div
                    id={MAIN_CONTENT_AREA_ID}
                    // `scroll-smooth` is mostly for the form section menu.
                    className="relative flex h-full flex-col overflow-y-auto scroll-smooth"
                >
                    {!isFullscreen && <GlobalAddButton />}
                    {/* Wraps the routed content too: the AI chat session must outlive the
                        `/ai-chat` route so a response keeps streaming after the user follows an
                        entity link out of it. */}
                    <AiChatSessionProvider>
                        {!isFullscreen && (
                            <LazyMotion features={motionFeatures} strict>
                                <MotionConfig reducedMotion="user">
                                    <FloatingAiAssistant prefersSideDock={isTimecardForm} />
                                </MotionConfig>
                            </LazyMotion>
                        )}
                        <OnboardingAssistant />
                        <Outlet />
                    </AiChatSessionProvider>
                    {!isFullscreen && <Footer />}
                </div>
            </main>
            {!isFullscreen && <TemporaryDrawer />}
            <LinkPeekHost />
            <ExportRibbon />
        </div>
    );
}

export default MainLayout;
